/**
 * MessageRenderer — converts agent markdown to clean HTML
 * Handles: **bold**, *italic*, ---divider, - bullets, numbered lists, line breaks
 * No external library. Keeps the bundle lean.
 */
export function MessageRenderer({ content }) {
  if (!content) return null

  // Split into paragraphs on double newline
  const blocks = content.split(/\n\n+/)

  return (
    <div className="message-body">
      {blocks.map((block, i) => <Block key={i} text={block} />)}
    </div>
  )
}

function Block({ text }) {
  const trimmed = text.trim()

  // Horizontal rule
  if (/^---+$/.test(trimmed)) {
    return <hr className="msg-hr" />
  }

  // Bullet list
  if (/^[-•]\s/.test(trimmed)) {
    const items = trimmed.split('\n').filter(l => /^[-•]\s/.test(l.trim()))
    return (
      <ul className="msg-list">
        {items.map((item, i) => (
          <li key={i}>{inlineFormat(item.replace(/^[-•]\s+/, ''))}</li>
        ))}
      </ul>
    )
  }

  // Numbered list
  if (/^\d+\.\s/.test(trimmed)) {
    const items = trimmed.split('\n').filter(l => /^\d+\.\s/.test(l.trim()))
    return (
      <ol className="msg-list">
        {items.map((item, i) => (
          <li key={i}>{inlineFormat(item.replace(/^\d+\.\s+/, ''))}</li>
        ))}
      </ol>
    )
  }

  // Regular paragraph — preserve single line breaks
  const lines = trimmed.split('\n')
  return (
    <p className="msg-para">
      {lines.map((line, i) => (
        <span key={i}>
          {inlineFormat(line)}
          {i < lines.length - 1 && <br />}
        </span>
      ))}
    </p>
  )
}

// Parse inline markdown: **bold**, *italic*, `code`
function inlineFormat(text) {
  const parts = []
  // Combined regex: **bold**, *italic*, `code`
  const re = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)/g
  let last = 0, match

  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index))
    if (match[1]) parts.push(<strong key={match.index}>{match[2]}</strong>)
    else if (match[3]) parts.push(<em key={match.index}>{match[4]}</em>)
    else if (match[5]) parts.push(<code key={match.index} className="msg-code">{match[6]}</code>)
    last = match.index + match[0].length
  }

  if (last < text.length) parts.push(text.slice(last))
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts
}
